import {
  type KeyboardEvent,
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  IconCustomSparkle,
  IconPlus,
  IconShipTogether,
  IconStandardBox,
  RedoOutlined,
  RightOutlined,
  ShareAltOutlined,
  WarningOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { Skeleton } from '../../../components/ui/Skeleton'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../../components/ui/rowActionsMenu'
import { useIsScrollable } from '../../../components/ui/useIsScrollable'
import {
  formatDate,
  formatPrice,
  formatPriceRange,
  formatRequirementsDetail,
  formatStockAvailability,
  resolveDrawingName,
} from '../format'
import { formatProcessLabel } from '../process-label'
import { canEditRequirements, resolveRowStatus } from '../row-status'
import type { ResolvedRowStatus } from '../row-status'
import type { LibraryPartRow, PartFilterStatus } from '../types'
import { PartRevBand } from './PartRevBand'
import { PartThumbnail } from './PartThumbnail'
import { VolumePricingCellTrigger } from './VolumePricingPopover'

/**
 * UID-225 (Parts Library visual-fidelity pass) — mirrors
 * `EditConfigurationModal.tsx`'s own `readStringAttr` (not imported: that
 * file owns the edit-config seam and isn't a shared module). Reads a
 * `configAttributes` string field the SAME way — `EditPartConfigRequest`'s
 * `process`/`material`/`finish`/`tolerance` (schema.gen.ts) — for the
 * REQUIREMENTS cell's process/detail line. Absent/non-string → `undefined`,
 * never a fabricated value.
 */
function readConfigAttr(
  attrs: Readonly<Record<string, unknown>> | undefined,
  key: string,
): string | undefined {
  const value = attrs?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

export interface PartsTableProps {
  readonly items: readonly LibraryPartRow[]
  readonly groupBySession: boolean
  /** UID-402 (ADR-0124) — Sourceable ids to mark as "just arrived from an
   * upload", for the Triage → "Review in library" hand-off. Purely additive
   * emphasis (a row class); it does NOT affect selection, ordering (the caller
   * owns that), or any action. Absent/empty ⇒ no row is marked. */
  readonly highlightedIds?: ReadonlySet<string> | undefined
  readonly selectedIds: ReadonlySet<string>
  /** UID-470 — the row checkbox's click handler. `shiftKey` carries the
   * modifier state from the DOM `click` event: real browsers dispatch that
   * SAME kind of event, with live modifier-key state, for both a mouse
   * click AND a Space-key activation of a focused checkbox — so this one
   * handler covers shift-click AND the Shift+Space keyboard equivalent
   * without any separate keydown wiring. `true` is a range-select request
   * (apply the result of toggling `id` to every row between it and the
   * caller's selection anchor, inclusive); `false`/omitted is a plain toggle
   * of just `id`. See `PartsLibraryPage.tsx`'s `toggleSelect` doc comment
   * for the anchor semantics this drives. */
  readonly onToggleSelect: (id: string, shiftKey?: boolean) => void
  readonly onToggleSelectAll: (ids: readonly string[], select: boolean) => void
  /** UID-234 — the NAME & DRAWING cell's part name is a real
   * `<Link to="/parts/:partId">` (registered `verticals/registry.tsx`) so
   * right-click/open-in-new-tab work, not a synthetic click handler; the
   * anchor navigates on its own. `onOpen`, when supplied, fires alongside
   * that navigation — the same optional-callback shape as `onReorder` /
   * `onEditConfig` — for a caller that wants to observe the open (e.g.
   * future analytics) without owning navigation itself. Absent → the link
   * still navigates; nothing else happens. */
  readonly onOpen?: ((partId: string) => void) | undefined
  /** Reorder a row's part (UID-65) — the primary-action cell's "+"
   * add-to-cart control for a `ready` row (UID-170/F2). UID-376: no longer
   * reachable from the ⋮ row menu (design's row menu carries exactly Share /
   * Create revision / Remove from library) — this remains a real prop
   * because the "+" primary-action control still uses it directly. */
  readonly onReorder?: ((id: string) => void) | undefined
  /** The Sourceable id whose reorder is currently in flight (disables its trigger). */
  readonly reorderingId?: string | null | undefined
  /** "Request volume RFQ" for a row whose Unit-price-cell popover (moved
   * from the removed Qty column, UID-386) is showing the above-cap
   * affordance (UID-159/A3, ADR-0058 §3) — the SAME queue-into-
   * `/rfq/new` path `PartsLibraryPage.handleAddToRfq` uses. Reused verbatim
   * (UID-170/F2) as the primary-action cell's "Request quote" CTA for a
   * `needs_quote` row — same RFQ affordance, a second entry point. */
  readonly onRequestVolumeRfq: (item: LibraryPartRow) => void
  /** Opens `EditConfigurationModal` for a row (UID-163/C3) — the
   * primary-action cell's "Configure" CTA for a `needs_config` row, and the
   * REQUIREMENTS cell's pencil icon for any editable row. UID-376: no longer
   * reachable from the ⋮ row menu (see `onReorder`'s doc comment above). */
  readonly onEditConfig?: ((item: LibraryPartRow) => void) | undefined
  /** UID-170/F2, UID-376 (screens.md §5 row actions, design's exact ⋮ menu
   * order: Share part / Create revision / Remove from library). "Share part"
   * has no backend endpoint yet — the caller wires the same stub-toast
   * `PartDetailPage`'s "Share part" uses. The ⋮ menu renders the item only
   * when a real destination is wired; absent, no item. */
  readonly onShare?: ((item: LibraryPartRow) => void) | undefined
  /** "Remove from library" — no backend removal endpoint exists yet in
   * `PartsLibraryService` (flagged; see `PartsLibraryPage`'s wiring), so the
   * caller wires the same stub-toast posture as `onShare`. Same
   * optional-callback shape — absent, no item. */
  readonly onRemoveFromLibrary?: ((item: LibraryPartRow) => void) | undefined
  /** Opens `CreateRevisionModal` for a row (UID-167/D3, wired UID-376) —
   * same optional-callback shape as `onShare`/`onRemoveFromLibrary`; absent,
   * the ⋮ menu omits the item. */
  readonly onCreateRevision?: ((item: LibraryPartRow) => void) | undefined
  /** UID-405 (design-parity audit fix) — the primary-action cell's "Find
   * match" CTA for a `needs_match` row (design `Fictiv Platform.dc.html
   * :8623,7693`'s `it.onAction` -> `this.go('catalog')` for `needs-match`).
   * Same optional-callback/safe-no-op posture as `onReorder`/`onEditConfig`
   * above — absent, the button still renders (per the AC every non-
   * analyzing status gets a real control) but is a no-op with a title
   * explaining why. */
  readonly onFindMatch?: ((item: LibraryPartRow) => void) | undefined
}

/**
 * Design-review punch list item 13 — the row status chip's visible LABEL,
 * remapped onto the design's `libStateCfg` 6-label readiness vocabulary
 * (Priced / Auto-configured / Needs config / Needs quote / Quoted /
 * Needs match). This app's `PartFilterStatus` only carries 4 of those 6
 * states — mapped to their closest counterpart:
 *   - `ready`  -> "Priced"       (closest: a ready row already has a price)
 *   - `config` -> "Needs config"
 *   - `quote`  -> "Needs quote"
 *   - `match`  -> "Needs match"  (unchanged — already an exact match)
 * "Auto-configured" and "Quoted" have NO counterpart in this app's
 * `PartFilterStatus`/`LibraryRowStatus` contract — flagged here (and in the
 * story report) rather than fabricated onto an existing state; introducing
 * them for real needs a backend sub-state this contract doesn't send today.
 */
const FILTER_STATUS_LABEL: Record<PartFilterStatus, string> = {
  ready: 'Priced',
  config: 'Needs config',
  quote: 'Needs quote',
  match: 'Needs match',
}

/**
 * UID-225 (CSS-fidelity pass, design's `libStateCfg`) — maps this app's
 * 4-state `PartFilterStatus` onto the closest of the design's 6 readiness
 * colors: `ready`→"priced" (green) and `config`→"needs-config" (orange)
 * already matched exactly pre-pass; `quote`→"needs-rfq" now uses the
 * design's distinct violet (`violet` tone, #8b5cf6) rather than the app's
 * resolved brand-purple (`purple` tone, #9747FF — untouched everywhere
 * else); `match`→"needs-match" now uses the design's neutral slate
 * (`slate` tone) rather than the pre-pass `error` (red) tone, which had no
 * basis in the design (this state is "no catalog match found", not an
 * error). The design's other two readiness colors — "auto-configured"
 * (blue) and "quoted" (darker green) — have no corresponding sub-state in
 * this app's `PartFilterStatus`/`LibraryRowStatus` contract (flagged, not
 * fabricated).
 */
const FILTER_STATUS_TONE: Record<PartFilterStatus, 'success' | 'warning' | 'violet' | 'slate'> = {
  ready: 'success',
  config: 'warning',
  quote: 'violet',
  match: 'slate',
}

/**
 * The Parts Library table (screens.md §5: "table rows (thumbnail, name+
 * drawing, requirements/source, price+RFQ-badge, qty, action +/⋮)"),
 * optionally split into per-upload-session groups ("Group by session"
 * toggle). A real `<table>` (not `<div>` rows) so the row/column semantics
 * are available to assistive tech for free — components.md's "Table / data
 * row" primitive doesn't exist yet in `apps/web`, so this screen builds the
 * markup directly rather than inventing a second ad-hoc row layout.
 */
export function PartsTable({
  items,
  groupBySession,
  highlightedIds,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpen,
  onReorder,
  reorderingId,
  onRequestVolumeRfq,
  onEditConfig,
  onShare,
  onRemoveFromLibrary,
  onCreateRevision,
  onFindMatch,
}: PartsTableProps) {
  if (!groupBySession) {
    return (
      <PartsTableGroup
        items={items}
        highlightedIds={highlightedIds}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        onToggleSelectAll={onToggleSelectAll}
        onOpen={onOpen}
        onReorder={onReorder}
        reorderingId={reorderingId}
        onRequestVolumeRfq={onRequestVolumeRfq}
        onEditConfig={onEditConfig}
        onShare={onShare}
        onRemoveFromLibrary={onRemoveFromLibrary}
        onCreateRevision={onCreateRevision}
        onFindMatch={onFindMatch}
      />
    )
  }

  const groups = new Map<string, LibraryPartRow[]>()
  for (const item of items) {
    const key = item.sessionId ?? 'ungrouped'
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }

  return (
    <div className="parts-table-groups">
      {[...groups.entries()].map(([sessionId, groupItems]) => {
        const first = groupItems[0]
        // UID-225: render the session's HUMAN display name — never the raw
        // grouping id, which must not leak into the UI. Falls back to a
        // neutral label (not the id) for the ungrouped bucket or for a row
        // whose producer hasn't populated `sessionName` yet.
        const label =
          sessionId === 'ungrouped'
            ? 'Not part of an upload session'
            : (first?.sessionName ?? 'Upload session')
        // Meta line (design intent: "6 parts · uploaded May 30") — part count
        // from the group size, date from the session's created/uploaded time.
        const uploadedOn = first?.sessionCreatedAt ? formatDate(first.sessionCreatedAt) : null
        const meta =
          sessionId === 'ungrouped'
            ? null
            : `${groupItems.length} ${groupItems.length === 1 ? 'part' : 'parts'}${
                uploadedOn ? ` · uploaded ${uploadedOn}` : ''
              }`
        return (
          <PartsSessionGroupCard
            key={sessionId}
            groupId={sessionId}
            label={label}
            meta={meta}
            items={groupItems}
            highlightedIds={highlightedIds}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            onOpen={onOpen}
            onReorder={onReorder}
            reorderingId={reorderingId}
            onRequestVolumeRfq={onRequestVolumeRfq}
            onEditConfig={onEditConfig}
            onShare={onShare}
            onRemoveFromLibrary={onRemoveFromLibrary}
            onCreateRevision={onCreateRevision}
            onFindMatch={onFindMatch}
          />
        )
      })}
    </div>
  )
}

/**
 * Design-review punch list item 1 — one `.parts-group-card` per session
 * (`Fictiv Platform.dc.html` ~L1241-1252): a header bar (icon badge +
 * session name/meta + a group select-all checkbox + a collapse chevron +
 * a right-aligned contextual CTA) wrapping the group's own
 * `PartsTableGroup` table, rendered exactly once per group — never
 * duplicated within it. Collapse state is local/presentational (no
 * caller-visible behavior change): collapsing just stops rendering this
 * group's table, it never drops the group's own selection.
 */
const PartsSessionGroupCard = memo(function PartsSessionGroupCard({
  groupId,
  label,
  meta,
  items,
  highlightedIds,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpen,
  onReorder,
  reorderingId,
  onRequestVolumeRfq,
  onEditConfig,
  onShare,
  onRemoveFromLibrary,
  onCreateRevision,
  onFindMatch,
}: Omit<PartsTableProps, 'groupBySession' | 'items'> & {
  readonly groupId: string
  readonly label: string
  readonly meta: string | null
  readonly items: readonly LibraryPartRow[]
}) {
  const [collapsed, setCollapsed] = useState(false)
  const headingId = `parts-group-${groupId}`
  const panelId = `parts-group-panel-${groupId}`
  // UID-654 (table-responsiveness pass) — `ids`/`allSelected`/`someSelected`
  // and the two CTA descriptors below are derived purely from `items` +
  // `selectedIds`/`onReorder`/`onRequestVolumeRfq` (this card's own props) —
  // `useMemo` so a re-render triggered by an UNRELATED sibling group's state
  // change doesn't recompute them, and the `onClick` closures stay stable
  // across renders where the group's own contents didn't change.
  const ids = useMemo(() => items.map((item) => item.sourceable.id), [items])
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id))
  const someSelected = !allSelected && ids.some((id) => selectedIds.has(id))

  // UID-405 (design-parity audit fix, finding A5) — the design renders
  // BOTH right-aligned contextual CTAs independently when applicable
  // (`Fictiv Platform.dc.html:8688-8692`'s `sec.hasRfq`/`sec.hasReady` are
  // two separate `sc-if`s, never either/or), not the pre-UID-405 single
  // `cta` that picked one and dropped the other for a session with both
  // needs_quote AND ready rows. Labels match the design literally
  // ("Bundle N for RFQ" / "Add N to cart"), replacing this build's
  // previous "Request quote for N" / "Add ready to cart" wording.
  const needsQuoteItems = useMemo(
    () => items.filter((item) => resolveRowStatus(item) === 'needs_quote'),
    [items],
  )
  const readyIds = useMemo(
    () =>
      items.filter((item) => resolveRowStatus(item) === 'ready').map((item) => item.sourceable.id),
    [items],
  )

  const handleRfqCtaClick = useCallback(() => {
    for (const item of needsQuoteItems) onRequestVolumeRfq(item)
  }, [needsQuoteItems, onRequestVolumeRfq])
  const handleReadyCtaClick = useCallback(() => {
    if (!onReorder) return
    for (const id of readyIds) onReorder(id)
  }, [readyIds, onReorder])

  const rfqCta = useMemo(
    () =>
      needsQuoteItems.length > 0
        ? { label: `Bundle ${needsQuoteItems.length} for RFQ`, onClick: handleRfqCtaClick }
        : null,
    [needsQuoteItems, handleRfqCtaClick],
  )
  const readyCta = useMemo(
    () =>
      readyIds.length > 0 && onReorder
        ? { label: `Add ${readyIds.length} to cart`, onClick: handleReadyCtaClick }
        : null,
    [readyIds, onReorder, handleReadyCtaClick],
  )

  return (
    <div className="parts-group-card">
      <div className="parts-group-card-header">
        <label className="parts-group-select-all-hit">
          <input
            type="checkbox"
            className="checkbox-input"
            aria-label={`Select all parts in ${label}`}
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected
            }}
            onChange={(event) => onToggleSelectAll(ids, event.target.checked)}
          />
        </label>
        <button
          type="button"
          className="parts-group-chevron"
          aria-expanded={!collapsed}
          aria-controls={panelId}
          aria-label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? <RightOutlined aria-hidden="true" /> : <DownOutlined aria-hidden="true" />}
        </button>
        <span className="parts-group-icon-badge" aria-hidden="true">
          <IconShipTogether size={16} />
        </span>
        <div className="parts-group-heading-block">
          <h3 id={headingId} className="parts-group-heading">
            {label}
          </h3>
          {meta && <p className="caption-text parts-group-meta">{meta}</p>}
        </div>
        <span className="parts-library-toolbar-spacer" />
        {/* UID-405 — both render together when both apply (design
            `:8688-8692`); each is its own `<Button>` with the design's own
            treatment, not the shared generic `secondary` variant the
            single pre-UID-405 `cta` used for either case. */}
        {rfqCta && (
          <Button
            variant="outline-purple"
            size="sm"
            className="parts-group-cta parts-group-cta-rfq"
            onClick={rfqCta.onClick}
          >
            {rfqCta.label}
          </Button>
        )}
        {readyCta && (
          <Button
            variant="primary"
            size="sm"
            className="parts-group-cta parts-group-cta-ready"
            onClick={readyCta.onClick}
          >
            {readyCta.label}
          </Button>
        )}
      </div>
      {!collapsed && (
        <div id={panelId}>
          <PartsTableGroup
            items={items}
            highlightedIds={highlightedIds}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onToggleSelectAll={onToggleSelectAll}
            onOpen={onOpen}
            onReorder={onReorder}
            reorderingId={reorderingId}
            onRequestVolumeRfq={onRequestVolumeRfq}
            onEditConfig={onEditConfig}
            onShare={onShare}
            onRemoveFromLibrary={onRemoveFromLibrary}
            onCreateRevision={onCreateRevision}
            onFindMatch={onFindMatch}
            nested
          />
        </div>
      )}
    </div>
  )
})

const PartsTableGroup = memo(function PartsTableGroup({
  items,
  highlightedIds,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpen,
  onReorder,
  reorderingId,
  onRequestVolumeRfq,
  onEditConfig,
  onShare,
  onRemoveFromLibrary,
  onCreateRevision,
  onFindMatch,
  nested,
}: Omit<PartsTableProps, 'groupBySession'> & { readonly nested?: boolean }) {
  // UID-654 — same `useMemo` rationale as `PartsSessionGroupCard` above.
  const ids = useMemo(() => items.map((item) => item.sourceable.id), [items])
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id))
  const someSelected = !allSelected && ids.some((id) => selectedIds.has(id))
  const handleToggleSelectAll = useCallback(
    (event: { target: { checked: boolean } }) => onToggleSelectAll(ids, event.target.checked),
    [ids, onToggleSelectAll],
  )
  const cardRef = useRef<HTMLDivElement>(null)
  const isScrollable = useIsScrollable(cardRef)
  const cardClassName = [
    'parts-table-card',
    'parts-table-card--library',
    nested && 'parts-table-card-nested',
    isScrollable && 'is-scrollable',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={cardRef} className={cardClassName}>
      <table className="parts-table parts-table--library">
        {/* UID-405 (design-parity audit fix, finding B/7) — column-width
            hints mirroring the design's per-column flex-basis/min-width
            (`Fictiv Platform.dc.html:1380-1388`: thumb 64 / name flex:2 1
            180 min 170 / requirements flex:3 1 180 min 170 / source 92 /
            price 140 / action 120 / menu 34 — but see UID-618 below: price
            is now 200, a deliberate deviation). Plain `table-layout:auto`
            (default) had NO width control on any column at all — the
            browser's auto-layout algorithm was left to squeeze the two
            unconstrained flexible columns (Name & drawing, Requirements)
            at a 14"-laptop viewport (~1280-1440px), with no scroll
            affordance until 768px (`.parts-table-card`'s old
            `@media (max-width:768px)` cap, parts.css). These hints, PLUS
            `.parts-table--library`'s corrected `min-width` (parts.css,
            UID-470 — the original 960px undershot the true 1164px sum of
            these hints + cell padding; UID-618 widened price 140->200, so
            that sum is now 1224) and the now-viewport-independent
            `overflow-x:auto` + scroll-shadow, replace crushed/overflowing-
            with-no-cue columns with a horizontal scroll that has a visible
            cue, at any width below the table's real minimum content
            width — never a silent overflow. `parts-table--library` also
            promotes these six hints to enforced `min-width`s (parts.css)
            so the auto-layout algorithm can't shortchange them in favor of
            columns 3/4's own enforced floor once space is tight. */}
        <colgroup>
          <col className="parts-col-checkbox" />
          <col className="parts-col-thumb" />
          <col className="parts-col-name" />
          <col className="parts-col-requirements" />
          <col className="parts-col-source" />
          <col className="parts-col-price" />
          <col className="parts-col-action" />
          <col className="parts-col-menu" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              <label className="parts-row-checkbox-hit">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  aria-label="Select all parts in this group"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected
                  }}
                  onChange={handleToggleSelectAll}
                />
              </label>
            </th>
            <th scope="col">Part</th>
            <th scope="col">Name &amp; drawing</th>
            <th scope="col">Requirements</th>
            <th scope="col">Source</th>
            <th scope="col">Unit price</th>
            <th scope="col">
              <span className="sr-only">Action</span>
            </th>
            <th scope="col">
              <span className="sr-only">More actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const id = item.sourceable.id
            const name = item.sourceable.name ?? '(unnamed part)'
            const isCustom = item.sourceable.provenance === 'fictiv_custom'
            const isMisumiStandard = item.sourceable.provenance === 'misumi_standard'
            const chips = item.projects
            const attrs = item.sourceable.configAttributes
            // REQUIREMENTS cell (UID-225): prefer the row's own typed config
            // (`configAttributes` — set once a user saves `EditConfigurationModal`),
            // then the DFM-suggested process/material for a not-yet-configured
            // custom part, then fall back to the existing free-text
            // `requirements` summary every row always has. Never fabricated —
            // `tolerance` has no producer anywhere in the FE today (contract
            // gap flagged in the story report), so it's simply omitted rather
            // than invented.
            const reqProcess =
              readConfigAttr(attrs, 'process') ?? item.sourceable.dfmSummary?.suggestedProcess
            const reqMaterial =
              readConfigAttr(attrs, 'material') ?? item.sourceable.dfmSummary?.suggestedMaterial
            const reqTolerance = readConfigAttr(attrs, 'tolerance')
            const reqFinish = readConfigAttr(attrs, 'finish')
            // Show the human-readable process label — a stored enum token like
            // `cnc_machining` renders as `CNC machining` (UID-334); legacy
            // free-text (`CNC Milling`) passes through unchanged.
            // UID-577 — a Buy row's datasheet is always available and is
            // DERIVED when the server sent no filename; a Make row's drawing is
            // only ever the real upload (design `drawingInfo`,
            // `Fictiv Platform.dc.html:7479-7491`).
            const drawingName = resolveDrawingName({
              isCustom,
              drawingFilename: item.drawingFilename,
              catalogSku: item.sourceable.catalogSku ?? null,
              name: item.sourceable.name ?? null,
            })
            // UID-577 — the design's `noExtract` (`Fictiv Platform.dc.html
            // :8636`): a custom part that still needs configuring and has NO
            // drawing to extract requirements from has nothing to summarise, so
            // the cell states that outright instead of echoing a stale
            // free-text `requirements` blurb.
            const noExtract =
              isCustom && resolveRowStatus(item) === 'needs_config' && drawingName == null
            const reqPrimary = noExtract
              ? 'Needs configuration'
              : reqProcess != null
                ? formatProcessLabel(reqProcess)
                : item.requirements
            // UID-577 — the sub-line splits by provenance exactly as the
            // design's `reqSubArr` does (`Fictiv Platform.dc.html:8640-8643`):
            // a Make row summarises its material/tolerance/finish, a Buy row
            // shows catalog availability. The Buy branch was missing entirely,
            // so no catalog row had a sub-line. A `noExtract` row has nothing
            // to summarise either way.
            const stock = isCustom
              ? null
              : formatStockAvailability({
                  leadTime: item.leadTime,
                  volumeCap: item.sourceable.pricingProjection?.volumeCurve?.cap ?? null,
                })
            const reqDetail = noExtract
              ? null
              : isCustom
                ? reqProcess != null
                  ? formatRequirementsDetail([reqMaterial, reqTolerance, reqFinish])
                  : null
                : (stock?.text ?? null)
            // UID-577 — the pencil is ALSO gated on the row's READINESS state
            // (design `canEditReq`, `Fictiv Platform.dc.html:8614`). Provenance
            // alone was the wrong axis: it let a `needs_config` row show the
            // pencil, the "Configure" CTA AND the "Needs config" chip all at
            // once (Slack 2026-09-10: "here both are coming, and also the
            // chip"). An edit affordance needs something to edit.
            //
            // `!isMisumiStandard` DELIBERATELY STAYS, and is a knowing deviation
            // from the design, which shows the pencil on catalog rows too
            // (dc.html:8614 does not test provenance). In the prototype
            // `onEditReq` opens one generic `openConfig` modal; in this app
            // `onEditConfig` opens `EditConfigurationModal` — a MANUFACTURING
            // configurator (process/material/finish/DFM via
            // `PartConfiguratorShell`, `PART_LIBRARY_CAPABILITIES`). Pointing it
            // at a purchased MISUMI catalog part would offer process/material
            // edits that part has no meaning for, and `PATCH /parts/{partId}`
            // has no catalog-configuration counterpart to save them to. The
            // Buy-row pencil (UID-577 item B2) therefore needs a real
            // catalog-configuration surface first — deferred, not dropped.
            const showEditPencil =
              Boolean(onEditConfig) && !isMisumiStandard && canEditRequirements(item)
            return (
              // Design-review punch list item 15: no more `parts-row-selected`
              // full-row tint — selection is conveyed by the checkbox + the
              // bulk action bar alone.
              //
              // UID-402 — `parts-row-from-upload` is NOT a reintroduction of
              // that: it marks rows that just arrived from a Triage hand-off
              // (a transient, navigation-scoped fact the customer caused), not
              // the live selection state the punch-list item was about. The
              // hand-off banner above the table carries the same information in
              // text, so this is never the sole signal.
              <tr
                key={id}
                className={highlightedIds?.has(id) ? 'parts-row-from-upload' : undefined}
              >
                <td>
                  <label className="parts-row-checkbox-hit">
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      aria-label={`Select ${name}`}
                      checked={selectedIds.has(id)}
                      // UID-470 — `onClick` (not just `onChange`) so this
                      // reads `event.shiftKey`: a `click` event carries live
                      // modifier-key state for a mouse click AND for a
                      // Space-key activation of this focused checkbox alike
                      // — see `PartsTableProps.onToggleSelect`'s doc comment
                      // — so this single handler is also the WCAG-required
                      // keyboard equivalent, not just the mouse path.
                      //
                      // Deliberately does NOT `preventDefault()`: a
                      // checkbox's browser/jsdom "canceled activation steps"
                      // revert `checked` back to its PRE-click value the
                      // instant the click event finishes dispatching, which
                      // stomps a same-tick React re-render — so a
                      // `preventDefault`'d version of this handler silently
                      // never visibly checks/unchecks the box. Leaving the
                      // native toggle alone is harmless: it only ever
                      // flips THIS row to the exact state `onToggleSelect`
                      // computes anyway (a shift-click's OWN row toggles to
                      // its "resulting" state too), and the re-render from
                      // `selectedIds` changing is what actually flips every
                      // OTHER row in a shift-click range.
                      onClick={(event: MouseEvent<HTMLInputElement>) => {
                        onToggleSelect(id, event.shiftKey)
                      }}
                      // Native toggle fires a real `change` too (no
                      // `preventDefault` above) — nothing extra to do with
                      // it, but a controlled `checked` prop still requires
                      // an `onChange` or React warns.
                      onChange={() => {}}
                    />
                  </label>
                </td>
                <td>
                  <PartRowThumb thumbnailUrl={item.thumbnailUrl} rev={item.sourceable.rev} />
                </td>
                <td>
                  <div className="parts-row-name">
                    {/* UID-234 — a real anchor to the Part Detail page
                      (`/parts/:partId`, `verticals/registry.tsx`), not a
                      `<span>`: right-click/open-in-new-tab, middle-click, and
                      keyboard Tab+Enter all work natively. `stopPropagation`
                      keeps this click from bubbling into the `<tr>` (there is
                      no row-level handler today, but this cell now sits next
                      to several OTHER interactive controls — checkbox,
                      pencil, "+", ⋮ — each in its own `<td>`, so this guards
                      against a future row-level handler ever swallowing
                      those). */}
                    <Link
                      to={`/parts/${id}`}
                      className="parts-row-name-label"
                      title={name}
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpen?.(id)
                      }}
                    >
                      {name}
                    </Link>
                    {/* NAME & DRAWING subtitle (UID-225): the part number +
                      "· MISUMI" brand suffix, derived from real fields —
                      `catalogSku` and `provenance === 'misumi_standard'` —
                      never a hardcoded brand string for a non-MISUMI row. */}
                    {(item.sourceable.catalogSku || isMisumiStandard) && (
                      <span className="caption-text parts-row-subtitle">
                        {item.sourceable.catalogSku}
                        {item.sourceable.catalogSku && isMisumiStandard && ' · '}
                        {isMisumiStandard && 'MISUMI'}
                      </span>
                    )}
                    {drawingName && (
                      // UID-225: the prototype's inline attribute sets this icon
                      // stroke to var(--info) (blue, `Fictiv Platform.dc.html`
                      // ~L1284-1287), but the fix-list explicitly calls for a
                      // NEUTRAL grey chip (icon + label both fg-3/fg-2) here —
                      // rendered via `.parts-row-drawing-chip-icon` below rather
                      // than the link-blue the markup literally specifies.
                      <span className="parts-row-drawing-chip">
                        <span className="parts-row-drawing-chip-icon">
                          <FileTextOutlined aria-hidden="true" />
                        </span>
                        <span className="parts-row-drawing-chip-label">{drawingName}</span>
                      </span>
                    )}
                    {/* UID-297 — workspace/project membership chips
                      (screens.md §5), server read-back from `item.projects`
                      (`library.part_workspace`) — durable across a refresh. */}
                    {chips && chips.length > 0 && (
                      <span className="parts-row-project-chips">
                        {chips.map((workspace) => (
                          <Chip key={workspace.id} tone="neutral">
                            {workspace.name}
                          </Chip>
                        ))}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="parts-requirements-cell">
                    <span className="parts-requirements-primary">
                      <span className="parts-requirements-primary-text">{reqPrimary}</span>
                      {showEditPencil && (
                        <button
                          type="button"
                          className="icon-btn parts-requirements-edit-btn"
                          aria-label={`Edit requirements for ${name}`}
                          onClick={() => onEditConfig?.(item)}
                        >
                          <EditOutlined aria-hidden="true" />
                        </button>
                      )}
                    </span>
                    {reqDetail && (
                      // UID-577 — an in-stock line reads teal/semibold in the
                      // design (`reqSubStyle`, dc.html:8644). Scoped modifier
                      // class, NOT a change to `.caption-text` or to
                      // `.parts-requirements-cell`'s defaults: those are shared
                      // with `WorkspacePartsTab`, which must keep rendering as
                      // it does today.
                      <span
                        className={
                          stock?.inStock
                            ? 'caption-text parts-requirements-stock is-in-stock'
                            : 'caption-text'
                        }
                      >
                        {reqDetail}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="parts-source-cell">
                    <Chip tone={FILTER_STATUS_TONE[item.filterStatus]}>
                      {FILTER_STATUS_LABEL[item.filterStatus]}
                    </Chip>
                    {/* UID-225: "Custom" reads green in the design (a
                      `it.isCustom` sun/asterisk glyph on a green-tinted
                      chip) — `Chip`'s `success` tone (already AA-contrast
                      audited, tokens.css UID-47 note) is the existing green
                      token, reused rather than forking a new one. "Standard"
                      stays the neutral grey `Chip` tone it already used,
                      just with the package glyph added (icon+text always
                      together — WCAG 1.4.1, never color-only). */}
                    <Chip
                      tone={isCustom ? 'success' : 'neutral'}
                      icon={isCustom ? <IconCustomSparkle /> : <IconStandardBox />}
                    >
                      {isCustom ? 'Make' : 'Buy'}
                    </Chip>
                  </div>
                </td>
                <td>
                  {/* UID-654 — `item`/`onRequestVolumeRfq` passed RAW (not
                      pre-bound into a `() => ...` closure here in the loop):
                      that inline closure was a NEW function identity every
                      `PartsTableGroup` render, which defeated `PartPriceCell`'s
                      `React.memo` for every row whenever ANY other row's data
                      changed. Binding now happens inside the memoized
                      component itself (via `useCallback`), where the closure's
                      own identity churn is harmless. */}
                  <PartPriceCell item={item} name={name} onRequestVolumeRfq={onRequestVolumeRfq} />
                </td>
                <td>
                  {/* UID-654 — same rationale as `PartPriceCell` above: raw
                      `onReorder`/`onEditConfig`/`onRequestVolumeRfq`/
                      `onFindMatch` + `item`/`id`, not per-row closures. */}
                  <PartPrimaryAction
                    item={item}
                    name={name}
                    busy={reorderingId === id}
                    onReorder={onReorder}
                    onEditConfig={onEditConfig}
                    onRequestVolumeRfq={onRequestVolumeRfq}
                    onFindMatch={onFindMatch}
                  />
                </td>
                <td>
                  {/* UID-654 — same rationale as `PartPriceCell`/
                      `PartPrimaryAction` above: raw `item` +
                      `onShare`/`onRemoveFromLibrary`/`onCreateRevision`. */}
                  <PartRowActions
                    item={item}
                    partName={name}
                    reordering={reorderingId === id}
                    onShare={onShare}
                    onRemoveFromLibrary={onRemoveFromLibrary}
                    onCreateRevision={onCreateRevision}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
})

/**
 * UID-405 (design-parity audit fix, finding A) — the design's `libStateCfg`
 * (`Fictiv Platform.dc.html:7687-7694`) `action` label per resolved state,
 * literal text. `needs_quote`'s label is now "Add to RFQ" (was "Request
 * quote" — the audit's finding A4 flagged this app rendering the WRONG one
 * of the design's two distinct labels for two distinct states: `needs-rfq`
 * -> "Add to RFQ", vs. an EXPIRED `quoted` row -> "Request quote", design
 * `:8611,8621`). `bundled`/`needs_match`/`quote_expired` are the three
 * states UID-405 additionally folds in end-to-end (see `row-status.ts`'s
 * `ResolvedRowStatus` doc comment).
 */
const PRIMARY_ACTION_LABEL: Record<Exclude<ResolvedRowStatus, 'analyzing'>, string> = {
  ready: 'Add to cart',
  needs_config: 'Configure',
  needs_quote: 'Add to RFQ',
  needs_match: 'Find match',
  bundled: 'Bundled',
  quote_expired: 'Request quote',
}

/**
 * Primary-action cell (UID-170/F2, `ResolvedRowStatus`-driven — screens.md
 * §5 / dc.html:1142,1195-1225,1342-1362,1374-1375, extended UID-405 to the
 * design's full `libStateCfg` state set). Renders EXACTLY ONE control per
 * row, keyed off `resolveRowStatus`:
 *   - `ready` → the "+" add-to-cart button (reuses `onReorder`'s real POST,
 *     see the prop's doc comment on `PartsTableProps`);
 *   - `needs_config` → "Configure" (reuses `onEditConfig`, opens C3's modal);
 *   - `needs_quote` → "Add to RFQ" (reuses `onRequestVolumeRfq`, the A3 RFQ
 *     affordance);
 *   - `quote_expired` → "Request quote" (SAME affordance/style as
 *     `needs_quote` — outlined violet — only the label differs, design
 *     `:8611,8621,8625-8627`: an expired row's `actKey` resolves to
 *     `'needs-rfq'` too, it just gets the "Request quote" label instead of
 *     "Add to RFQ");
 *   - `needs_match` → "Find match" (neutral/default button, design `:8623`
 *     — reuses `onFindMatch`, UID-405's new optional prop);
 *   - `bundled` → a disabled "Bundled" chip (design `:8618` — never wired to
 *     a real handler; a bundled part must be purchased with the rest of its
 *     RFQ, so there is no single-row action here by design);
 *   - `analyzing` → a shimmer placeholder, never a dead/disabled button.
 * Every non-analyzing, non-bundled button is a REAL `<button>` with a text
 * label (never color/icon-only, WCAG 3.3.1/1.4.1) — the "+" carries an
 * `aria-label` naming the part since its visible glyph alone isn't a label;
 * "Bundled" is a `disabled` button (not a styled `<span>`) so it still
 * exposes its label/state to assistive tech via the native disabled
 * semantics, matching the design's `cursor:not-allowed` intent.
 */
// UID-654 (table-responsiveness pass) — takes the RAW top-level
// `onReorder`/`onEditConfig`/`onRequestVolumeRfq`/`onFindMatch` callbacks
// (`PartsTableProps`'s own, stable across a `PartsLibraryPage` re-render) plus
// `item`, rather than a per-row `() => handler(item)` closure the CALLER
// binds — the old shape meant `PartsTableGroup`'s `.map()` created a brand
// new closure for every one of these props on every render, which defeated
// this component's own `React.memo` for every row whenever ANY other row's
// data changed (a new function is never `===` the previous one). Binding
// happens HERE instead, in `useCallback`s scoped to this one memoized
// component instance — a new closure's identity churning inside a memoized
// leaf is harmless, since nothing downstream compares it for bail-out.
const PartPrimaryAction = memo(function PartPrimaryAction({
  item,
  name,
  busy,
  onReorder,
  onEditConfig,
  onRequestVolumeRfq,
  onFindMatch,
}: {
  readonly item: LibraryPartRow
  readonly name: string
  readonly busy: boolean
  readonly onReorder?: ((id: string) => void) | undefined
  readonly onEditConfig?: ((item: LibraryPartRow) => void) | undefined
  readonly onRequestVolumeRfq: (item: LibraryPartRow) => void
  readonly onFindMatch?: ((item: LibraryPartRow) => void) | undefined
}) {
  const status = resolveRowStatus(item)
  const id = item.sourceable.id
  const handleAddToCart = useCallback(() => onReorder?.(id), [onReorder, id])
  const handleConfigure = useCallback(() => onEditConfig?.(item), [onEditConfig, item])
  const handleFindMatch = useCallback(() => onFindMatch?.(item), [onFindMatch, item])
  const handleRequestQuote = useCallback(() => onRequestVolumeRfq(item), [onRequestVolumeRfq, item])

  if (status === 'analyzing') {
    return (
      <span className="parts-row-primary-shimmer" aria-busy="true">
        {/* `Skeleton` already neutralizes its sweep under
            `prefers-reduced-motion: reduce` (global.css) — no second
            motion rule needed here. */}
        <Skeleton width={72} height={20} />
        {/* Text (not just the shimmer) conveys the state — never
            color/animation-only (WCAG 1.4.1) — and `aria-live` announces it
            once rather than a screen reader re-reading every shimmering row. */}
        <span aria-live="polite">Analyzing…</span>
      </span>
    )
  }

  if (status === 'bundled') {
    // UID-405 — design `:8618,8624-8627`: a disabled chip, orange tint,
    // `cursor:not-allowed`. `title` carries the SAME explanatory copy the
    // design's `bundleMsg`/`flash()` toast would show on click, since a
    // native `disabled` button never fires `onClick` for a toast to hang
    // off of, and a disabled control still exposes its `title` on hover/
    // focus for sighted users; screen-reader users get the "why" from the
    // visible label + disabled state together (WCAG 4.1.2).
    return (
      <button
        type="button"
        className="btn parts-row-bundled-chip btn-sm parts-row-primary-btn"
        disabled
        title="This part is part of a bundled quote — it must be purchased together with the rest of that quote."
      >
        {PRIMARY_ACTION_LABEL.bundled}
      </button>
    )
  }

  if (status === 'needs_config') {
    // UID-225/UID-405: the design's solid warning-orange treatment with a
    // triangle-alert icon (`Fictiv Platform.dc.html` ~L1349
    // `it.urgentAction`) — every `needs_config` row produced by this app's
    // state machine maps to that same "config needed" urgency (there is no
    // separate non-urgent config sub-state to distinguish it from), so the
    // treatment applies unconditionally rather than gated on a field this
    // row type doesn't have.
    return (
      <button
        type="button"
        className="btn btn-warning btn-sm parts-row-config-btn parts-row-primary-btn"
        onClick={handleConfigure}
        title={onEditConfig ? undefined : 'Configuration isn’t available for this part yet'}
      >
        <WarningOutlined aria-hidden="true" />
        {PRIMARY_ACTION_LABEL.needs_config}
      </button>
    )
  }

  if (status === 'needs_match') {
    // UID-405 — design `:8623`: the default/neutral button treatment (the
    // ELSE branch of the design's `actionStyle` switch — no dedicated
    // "needs-match" color, unlike config/rfq), reusing the shared
    // `.btn-outline` neutral variant rather than inventing a new one.
    return (
      <button
        type="button"
        className="btn btn-outline btn-sm parts-row-primary-btn"
        onClick={handleFindMatch}
        title={
          onFindMatch ? undefined : 'Finding a catalog match isn’t available for this part yet'
        }
      >
        {PRIMARY_ACTION_LABEL.needs_match}
      </button>
    )
  }

  if (status === 'needs_quote' || status === 'quote_expired') {
    // UID-225: an OUTLINED purple treatment (white fill, purple border+text),
    // not the solid `.btn-purple` fill — the design's RFQ CTAs (row primary
    // action + bulk bar) are outlined, only the bulk bar's "Add to RFQ
    // bundle" / the RFQ-bundle banner's "Review & request quote" stay solid.
    // UID-405 — `parts-row-rfq-btn` layers the LITERAL design hex
    // (`#8b5cf6`/`#d7c6f7`) on top of the shared AA-safe `.btn-outline-purple`
    // base; see that class's comment in parts.css for the accepted-risk
    // rationale (human ruling, same posture as UID-325's precedent).
    return (
      <button
        type="button"
        className="btn btn-outline-purple btn-sm parts-row-rfq-btn parts-row-primary-btn"
        onClick={handleRequestQuote}
      >
        {PRIMARY_ACTION_LABEL[status]}
      </button>
    )
  }

  return (
    <button
      type="button"
      className="icon-btn parts-row-add-btn"
      aria-label={`Add ${name} to cart`}
      aria-busy={busy}
      disabled={busy}
      onClick={handleAddToCart}
      title={onReorder ? undefined : 'Adding to cart isn’t available for this part yet'}
    >
      <IconPlus size={18} strokeWidth={2.2} />
    </button>
  )
})

/**
 * UNIT PRICE cell (UID-225, `LibraryRowStatus`-driven — same status source
 * as the primary-action cell). Design ref: `srcn-part-library-listing-page.png`
 * — a `ready` row shows a real price (a range across `volumeCurve.tiers`
 * when present, else the plain `indicativePrice`) plus a subtext line built
 * ONLY from fields the row actually carries (never a fabricated "by lead
 * time"/"vol. breaks" for a row that has neither `indicativeLeadTimeDays`
 * nor a curve); `needs_quote`/`needs_config` swap the amount for the same
 * state text the row's status already conveys elsewhere (no color-only
 * signal — WCAG 1.4.1); `analyzing` shimmers, matching the primary-action
 * cell's own shimmer rather than showing a dead price.
 *
 * UID-386 — the Qty column was removed; the "Volume pricing" trigger it used
 * to host (`VolumePricingCellTrigger`, UID-159/A3/ADR-0058) now renders as a
 * third line under the price/subtext, only for a `ready` row with a
 * `volumeCurve` present — moving the affordance, not dropping it.
 */
// UID-654 — same "raw callback + item, bind inside the memoized leaf" shape
// as `PartPrimaryAction` above; see that component's doc comment.
const PartPriceCell = memo(function PartPriceCell({
  item,
  name,
  onRequestVolumeRfq,
}: {
  readonly item: LibraryPartRow
  readonly name: string
  readonly onRequestVolumeRfq: (item: LibraryPartRow) => void
}) {
  const status = resolveRowStatus(item)
  const handleRequestRfq = useCallback(() => onRequestVolumeRfq(item), [onRequestVolumeRfq, item])
  const currency = item.sourceable.indicativePrice?.currency ?? 'USD'
  const curve = item.sourceable.pricingProjection?.volumeCurve
  const tiers = curve?.tiers

  if (status === 'analyzing') {
    return (
      <span className="parts-price-cell" aria-busy="true">
        <Skeleton width={64} height={16} />
      </span>
    )
  }

  if (status === 'needs_quote') {
    return (
      <span className="parts-price-cell">
        <span className="parts-price-main parts-price-state-quote">Request quote</span>
        {item.inRfq && <Chip tone="violet">In RFQ</Chip>}
      </span>
    )
  }

  if (status === 'needs_config') {
    return (
      <span className="parts-price-cell">
        <span className="parts-price-main parts-price-state-config">Config to price</span>
      </span>
    )
  }

  const priceLabel = useMemo(
    () =>
      tiers && tiers.length > 0
        ? formatPriceRange(currency, tiers)
        : item.sourceable.indicativePrice
          ? formatPrice(item.sourceable.indicativePrice)
          : null,
    [tiers, currency, item.sourceable.indicativePrice],
  )

  const subtext = useMemo(() => {
    const parts: string[] = ['per unit']
    if (tiers && tiers.length > 1) {
      parts.push('vol. breaks')
    } else if (item.sourceable.dfmSummary?.indicativeLeadTimeDays != null) {
      parts.push('by lead time')
    }
    return parts.join(' · ')
  }, [tiers, item.sourceable.dfmSummary?.indicativeLeadTimeDays])

  return (
    <span className="parts-price-cell">
      {priceLabel ? (
        <>
          <span className="parts-price-main">{priceLabel}</span>
          <span className="caption-text parts-price-sub">{subtext}</span>
          {curve && (
            <VolumePricingCellTrigger
              partName={name}
              quantity={item.quantity}
              currency={currency}
              curve={curve}
              sourceable={item.sourceable}
              onRequestRfq={handleRequestRfq}
            />
          )}
        </>
      ) : (
        <span className="caption-text">—</span>
      )}
      {item.inRfq && <Chip tone="violet">In RFQ</Chip>}
    </span>
  )
})

/**
 * Row thumbnail (UID-91, screens.md §5). Sits immediately next to the part
 * name text, so a real image is treated as decorative (`alt=""`) — the name
 * is already announced by the adjacent text, and a filename/description
 * here would just be redundant noise for screen-reader users. Falls back to
 * the existing styled placeholder box when the row has no `thumbnailUrl`
 * (e.g. the part hasn't been analyzed yet, or no image match exists).
 */
const PartRowThumb = memo(function PartRowThumb({
  thumbnailUrl,
  rev,
}: {
  readonly thumbnailUrl: string | null
  readonly rev?: string | null | undefined
}) {
  // UID-281 (Slice A of UID-256): the `.parts-row-thumb` frame is now a wrapper
  // so the custom-part revision band can sit INSIDE it, below the image. The
  // image-fit rules move onto the `<img>` alone (`.parts-row-thumb-img`);
  // `PartRevBand` renders nothing for catalog parts (no `rev`), so the frame is
  // visually unchanged for them. The frame stays decorative (`aria-hidden`) only
  // when it is truly empty — no image AND no revision band — preserving the
  // placeholder's prior a11y treatment without hiding a rendered band.
  const decorative = !thumbnailUrl && !rev
  return (
    <div className="parts-row-thumb" aria-hidden={decorative || undefined}>
      {/* Design-review punch list item 11 (latent bug): no `width`/`height`
        attributes here — those previously fixed the intrinsic image size to
        32x33 inside the 60px frame, so a real thumbnail never filled its box.
        Sizing is CSS-only (`.parts-row-thumb-img`,
        parts.css: `width:100%;aspect-ratio:1;object-fit:contain`).
        UID-517 follow-up: `PartThumbnail` (this vertical's own, below)
        replaces the hand-rolled `<img>`/empty-`<span>` pair — same real-image
        component (with onError fallback, a bonus this swap adds for free)
        and the same shared decorative glyph placeholder every other surface
        this ticket touched already uses, instead of a blank bordered box. */}
      <PartThumbnail url={thumbnailUrl} />
      <PartRevBand rev={rev} />
    </div>
  )
})

/**
 * Per-row overflow (⋮) menu (screens.md §5 row actions). UID-376 (design
 * gap fix) — the design specifies EXACTLY three items, in this order:
 * "Share part", "Create revision", "Remove from library". "Edit
 * configuration", "Reorder", and "Add to project" are NOT part of this
 * menu per the design — those remain reachable elsewhere on the row
 * (the primary-action cell's "+"/"Configure" controls, and the
 * REQUIREMENTS cell's pencil icon), just not from here.
 *
 * All three items now share the SAME optional-callback contract: rendered
 * only when the caller wires a real handler, omitted otherwise — no more
 * always-rendered-but-disabled special case for "Create revision" (Story D's
 * `CreateRevisionModal` has since merged and `PartsLibraryPage` wires it for
 * real, same posture as `PartDetailPage`).
 *
 * An ARIA menu (`role="menu"` + per-item `role="menuitem"`): opening moves
 * focus onto the first item, Up/Down/Home/End rove between items, Escape
 * closes and refocuses the trigger, and a pointerdown outside (or a window
 * scroll/resize — the panel is `position: fixed` post-UID-421 and can't
 * follow the trigger) closes it too. All via the shared `useRowActionsMenu`
 * hook (rowActionsMenu.tsx); the roving-focus handler below is local to this
 * ARIA-menu caller. The trigger is inert only while a reorder for this row
 * is in flight.
 *
 * Exported (UID-407) so `WorkspacePartsTab` — the Project detail page's
 * simpler Parts tab — can reuse the SAME menu instead of a second
 * implementation drifting from this one; the comp (`Fictiv Platform.dc.html`
 * `wsView.partItems`) renders an identical ⋮ menu there.
 */
export const PartRowActions = memo(function PartRowActions({
  item,
  partName,
  reordering,
  onShare,
  onRemoveFromLibrary,
  onCreateRevision,
}: {
  readonly item: LibraryPartRow
  readonly partName: string
  readonly reordering: boolean
  readonly onShare?: ((item: LibraryPartRow) => void) | undefined
  readonly onRemoveFromLibrary?: ((item: LibraryPartRow) => void) | undefined
  readonly onCreateRevision?: ((item: LibraryPartRow) => void) | undefined
}) {
  // UID-654 — same "raw callback + item, bind inside the memoized leaf" shape
  // as `PartPrimaryAction`/`PartPriceCell` above; see `PartPrimaryAction`'s
  // doc comment for why the caller no longer pre-binds these.
  const handleShare = useCallback(() => onShare?.(item), [onShare, item])
  const handleRemove = useCallback(() => onRemoveFromLibrary?.(item), [onRemoveFromLibrary, item])
  const handleCreateRevision = useCallback(() => onCreateRevision?.(item), [onCreateRevision, item])

  // UID-421 — was a hand-rolled `position: absolute` panel inside
  // `.parts-row-actions`, clipped by `.parts-table-card`'s `overflow-y:
  // hidden` for the last row (no reserved bottom padding). Now shares
  // `useRowActionsMenu`/`RowActionsMenuPortal` (rowActionsMenu.tsx) with
  // `OrderRowMenu`/`RfqRowMenu` — the panel portals to `<body>` as
  // `position: fixed`, so it can never be clipped by an ancestor's overflow.
  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()

  // UID-421 — portaling the panel to <body> takes it out of the trigger's DOM
  // subtree, so native Tab order no longer flows from the trigger into it (a
  // React portal only affects event bubbling, not DOM layout/tab order). This
  // menu declares `role="menu"`/`role="menuitem"` (unlike the shared hook's
  // other, roleless callers), which commits to the ARIA menu keyboard
  // contract — so opening it must move focus onto the first item itself, and
  // `handleMenuKeyDown` below must rove Up/Down/Home/End between items.
  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
  }, [open, panelRef])

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(currentIndex + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[(currentIndex - 1 + items.length) % items.length]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    }
  }

  // UID-407 code-review finding (S3): with every handler absent (no caller
  // wires this yet), the ⋮ trigger used to still render and open onto an
  // empty `role="menu"`. Hooks above still run every render either way.
  if (!onShare && !onRemoveFromLibrary && !onCreateRevision) {
    return null
  }

  return (
    <div ref={containerRef} className="parts-row-actions">
      <button
        type="button"
        ref={triggerRef}
        className="icon-btn"
        aria-label={`More actions for ${partName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={reordering}
        onClick={(event) => {
          event.stopPropagation()
          toggleOpen()
        }}
      >
        ⋮
      </button>
      <RowActionsMenuPortal
        open={open}
        coords={coords}
        menuId={menuId}
        panelRef={panelRef}
        role="menu"
        onKeyDown={handleMenuKeyDown}
      >
        {onShare && (
          <button
            type="button"
            role="menuitem"
            className="parts-row-menu-item"
            onClick={runAndClose(handleShare)}
          >
            {/* UID-405 (design-parity audit fix, finding C) — the design's
                three ⋮ items each carry a leading icon (`Fictiv Platform
                .dc.html:1475-1478`); this build previously rendered
                text-only menu items — not a broken icon lookup, the icons
                were simply never added. `ShareAltOutlined` is the SAME
                glyph `PartDetailPage.tsx`'s own "Share part" item reuses
                for this action, `aria-hidden` since the visible text label
                already names the action (WCAG 1.1.1 — decorative here). */}
            <ShareAltOutlined aria-hidden="true" className="parts-row-menu-item-icon" />
            Share part
          </button>
        )}
        {onCreateRevision && (
          <button
            type="button"
            role="menuitem"
            className="parts-row-menu-item"
            onClick={runAndClose(handleCreateRevision)}
          >
            {/* UID-405 — `RedoOutlined` (rotate/refresh), matching the
                design's rotate-arrow glyph for this item (`:1476`) — NOT
                `PartDetailPage.tsx`'s `PlusOutlined`, which is the wrong
                glyph for "Create revision" (a plus reads as "add", the
                design's icon reads as "redo/rotate"). */}
            <RedoOutlined aria-hidden="true" className="parts-row-menu-item-icon" />
            Create revision
          </button>
        )}
        {onRemoveFromLibrary && (
          <>
            {/* UID-405 — the design places a divider immediately before
                the destructive item (`:1477`, `height:1px;background:
                var(--border-2)`), separating it from Share/Create
                revision above. An `<hr>` carries the implicit
                `separator` role so assistive tech announces it as a
                divider (not a fourth, empty menu item) without tripping
                the focusable-interactive lint a bare role="separator"
                div does. */}
            <hr className="parts-row-menu-divider" />
            <button
              type="button"
              role="menuitem"
              // Design-review punch list item 8: destructive item gets the
              // additional `-danger` hover/focus treatment (red text) on
              // top of the shared `.parts-row-menu-item` styling.
              className="parts-row-menu-item parts-row-menu-item-danger"
              onClick={runAndClose(handleRemove)}
            >
              {/* UID-405 — `DeleteOutlined` (trash), the SAME glyph
                  `PartDetailPage.tsx`'s own "Remove from library" item
                  uses; colored red via `.parts-row-menu-item-danger
                  .parts-row-menu-item-icon` (parts.css), matching the
                  design's `fx-trash` red icon (`:1478`). */}
              <DeleteOutlined aria-hidden="true" className="parts-row-menu-item-icon" />
              Remove from library
            </button>
          </>
        )}
      </RowActionsMenuPortal>
    </div>
  )
})
